import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useTranslation, Trans } from "react-i18next";
import { Link, useLocation } from "react-router-dom";
import { Mail, Loader2, X, ShieldCheck, Sparkles } from "lucide-react";
import { useLocale } from "@/i18n/useLocale";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Storage keys
const SUBSCRIBED_KEY = "spiruva.newsletter.subscribed";   // persistent (localStorage)
const SHOWN_THIS_SESSION_KEY = "spiruva.newsletter.shown"; // session-only (sessionStorage)
const DISMISSED_RECENTLY_KEY = "spiruva.newsletter.dismissed_until"; // localStorage, suppresses for N days

const isValidEmail = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(e || "");

// Routes where we suppress the exit-intent modal (admin, blog reading flow, the gate itself)
const SUPPRESSED_PATHS = ["/admin", "/reserve-allocation", "/inquiry", "/privacy"];

const NewsletterModal = () => {
  const { t } = useTranslation();
  const { locale, L } = useLocale();
  const { pathname } = useLocation();

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", consent: false });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [sentTo, setSentTo] = useState("");

  // Check if we should ever show
  const isSuppressed = useCallback(() => {
    if (SUPPRESSED_PATHS.some((p) => pathname.startsWith(p) || pathname.startsWith(`/de${p}`) || pathname.startsWith(`/ja${p}`))) {
      return true;
    }
    try {
      if (localStorage.getItem(SUBSCRIBED_KEY)) return true; // already subscribed
      const until = localStorage.getItem(DISMISSED_RECENTLY_KEY);
      if (until && Date.now() < Number(until)) return true; // recently dismissed
      if (sessionStorage.getItem(SHOWN_THIS_SESSION_KEY)) return true; // already shown this session
      // Don't compete with DownloadGate
      if (document.querySelector('[data-testid="download-gate-modal"]')) return true;
    } catch { /* ignore */ }
    return false;
  }, [pathname]);

  const trigger = useCallback(() => {
    if (isSuppressed()) return;
    try { sessionStorage.setItem(SHOWN_THIS_SESSION_KEY, "1"); } catch { /* ignore */ }
    setOpen(true);
  }, [isSuppressed]);

  // ---------------- Exit-intent listeners ----------------
  useEffect(() => {
    if (isSuppressed()) return;

    // Wait ~10s before arming — avoid annoying users who just arrived
    let armed = false;
    const armTimer = setTimeout(() => { armed = true; }, 10_000);

    // Desktop: mouse leaves the viewport via the top edge
    const onMouseLeave = (e) => {
      if (!armed) return;
      if (e.clientY <= 6 && e.relatedTarget == null) {
        trigger();
      }
    };

    // Mobile: detect rapid upward scroll (likely heading for URL bar / back)
    let lastScroll = window.scrollY;
    let lastTs = Date.now();
    const onScroll = () => {
      if (!armed) return;
      if (window.innerWidth >= 1024) return; // desktop uses mouseleave
      const now = Date.now();
      const dy = window.scrollY - lastScroll;
      const dt = now - lastTs;
      // Fast upward scroll while > 600px down
      if (dy < -120 && dt < 400 && window.scrollY > 600) {
        trigger();
      }
      lastScroll = window.scrollY;
      lastTs = now;
    };

    // Tab hidden after meaningful time (~12s)
    const onVisibility = () => {
      if (!armed) return;
      if (document.visibilityState === "hidden") {
        trigger();
      }
    };

    document.addEventListener("mouseleave", onMouseLeave);
    window.addEventListener("scroll", onScroll, { passive: true });
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      clearTimeout(armTimer);
      document.removeEventListener("mouseleave", onMouseLeave);
      window.removeEventListener("scroll", onScroll);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [trigger, isSuppressed]);

  // ESC + body lock when open
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") close(); };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const close = () => {
    setOpen(false);
    // Soft-suppress for 14 days after dismiss
    try {
      localStorage.setItem(DISMISSED_RECENTLY_KEY, String(Date.now() + 14 * 86400_000));
    } catch { /* ignore */ }
  };

  const setField = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!isValidEmail(form.email)) e.email = t("newsletter.error_email");
    if (!form.consent) e.consent = t("newsletter.error_consent");
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/newsletter/subscribe`, {
        email: form.email.trim(),
        name: form.name.trim() || null,
        consent: true,
        locale,
        source: "exit-intent",
      });
      try {
        localStorage.setItem(SUBSCRIBED_KEY, JSON.stringify({
          email: data?.email || form.email.trim(),
          subscribedAt: new Date().toISOString(),
        }));
      } catch { /* ignore */ }
      setSentTo(data?.email || form.email.trim());
      setDone(true);
      toast.success(t("newsletter.success_title"));
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail?.code === "consent_required") {
        setErrors((e) => ({ ...e, consent: t("newsletter.error_consent") }));
      } else {
        toast.error(t("newsletter.error_submit"));
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 bg-brand-black/70 z-[75] flex items-center justify-center p-4 md:p-6"
      data-testid="newsletter-modal"
      role="dialog"
      aria-modal="true"
      onClick={(e) => { if (e.target === e.currentTarget) close(); }}
    >
      <div className="bg-white max-w-md w-full p-7 md:p-9 border border-brand-green/15 relative max-h-[92vh] overflow-y-auto">
        <button
          onClick={close}
          aria-label={t("newsletter.dismiss")}
          data-testid="newsletter-close"
          className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center text-brand-black/55 hover:text-brand-black"
        >
          <X size={18} />
        </button>

        {!done ? (
          <>
            <div className="w-12 h-12 bg-brand-gold/15 text-[#8a6e1f] border border-brand-gold/40 flex items-center justify-center mb-5">
              <Sparkles size={18} />
            </div>
            <p className="eyebrow mb-2">◦  {t("newsletter.eyebrow")}</p>
            <h2 className="font-display text-2xl md:text-3xl text-brand-black leading-tight" data-testid="newsletter-title">
              {t("newsletter.title")}
            </h2>
            <p className="text-sm text-brand-black/65 mt-3 leading-relaxed">
              {t("newsletter.subtitle")}
            </p>

            <form onSubmit={submit} className="mt-6 space-y-4" data-testid="newsletter-form" noValidate>
              <div>
                <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-brand-black/55">{t("newsletter.field_email")}</span>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setField("email", e.target.value)}
                  data-testid="newsletter-email"
                  className={`w-full mt-1 px-4 py-2.5 bg-brand-cream/40 border ${errors.email ? "border-red-400" : "border-brand-green/20"} focus:border-brand-teal focus:outline-none text-sm font-body`}
                />
                {errors.email && <p className="mt-1 text-xs text-red-600" data-testid="newsletter-email-error">{errors.email}</p>}
              </div>
              <div>
                <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-brand-black/55">{t("newsletter.field_name")}</span>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setField("name", e.target.value)}
                  data-testid="newsletter-name"
                  className="w-full mt-1 px-4 py-2.5 bg-brand-cream/40 border border-brand-green/20 focus:border-brand-teal focus:outline-none text-sm font-body"
                />
              </div>

              <label
                className={`flex items-start gap-3 cursor-pointer select-none p-3 border ${errors.consent ? "border-red-400" : "border-brand-green/15"} bg-brand-cream/30`}
                data-testid="newsletter-consent-label"
              >
                <input
                  type="checkbox"
                  checked={form.consent}
                  onChange={(e) => setField("consent", e.target.checked)}
                  data-testid="newsletter-consent"
                  className="mt-1 w-4 h-4 accent-brand-green shrink-0"
                />
                <span className="text-xs text-brand-black/80 leading-relaxed">
                  {t("newsletter.consent_label")}
                  <br />
                  <span className="text-[11px] text-brand-black/60">
                    {t("newsletter.consent_privacy_prefix")}{" "}
                    <Link
                      to={L("/privacy")}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="newsletter-privacy-link"
                      className="underline hover:text-brand-teal"
                    >
                      {t("newsletter.consent_privacy_link")}
                    </Link>{t("newsletter.consent_privacy_suffix")}
                  </span>
                </span>
              </label>
              {errors.consent && <p className="text-xs text-red-600" data-testid="newsletter-consent-error">{errors.consent}</p>}

              <div className="flex flex-col sm:flex-row gap-3 mt-2">
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="newsletter-submit"
                  className="flex-1 inline-flex items-center justify-center gap-2 bg-brand-gold text-brand-black px-5 py-3.5 text-xs uppercase tracking-[0.22em] font-medium hover:bg-brand-black hover:text-brand-cream transition disabled:opacity-60"
                >
                  {submitting ? <><Loader2 size={14} className="animate-spin" /> {t("newsletter.submitting")}</> : <><Mail size={14} /> {t("newsletter.submit")}</>}
                </button>
                <button
                  type="button"
                  onClick={close}
                  data-testid="newsletter-dismiss"
                  className="inline-flex items-center justify-center gap-2 border border-brand-green/25 text-brand-black/65 px-4 py-3.5 text-xs uppercase tracking-[0.22em] hover:text-brand-black hover:border-brand-green/50 transition"
                >
                  {t("newsletter.dismiss")}
                </button>
              </div>

              <p className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-brand-black/45 pt-1">
                <ShieldCheck size={11} /> {t("download_gate.privacy")}
              </p>
            </form>
          </>
        ) : (
          <div className="text-center py-4" data-testid="newsletter-success">
            <div className="inline-flex items-center justify-center w-14 h-14 bg-brand-green text-brand-cream mb-5">
              <Mail size={22} />
            </div>
            <h2 className="font-display text-2xl md:text-3xl text-brand-black leading-tight" data-testid="newsletter-success-title">
              {t("newsletter.success_title")}
            </h2>
            <p className="text-sm text-brand-black/75 mt-4 leading-relaxed" data-testid="newsletter-success-body">
              <Trans
                i18nKey="newsletter.success_body"
                values={{ email: sentTo }}
                components={{ strong: <strong className="text-brand-black" /> }}
              />
            </p>
            <button
              onClick={close}
              data-testid="newsletter-success-close"
              className="mt-7 inline-flex items-center justify-center gap-2 border border-brand-green/30 text-brand-black px-5 py-3 text-xs uppercase tracking-[0.22em] hover:bg-brand-green hover:text-brand-cream transition"
            >
              {t("newsletter.success_close")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default NewsletterModal;
